<template>
    <section dir="rtl" class="relative py-20 md:py-28 overflow-hidden bg-gradient-to-b from-[#EEF4FF] via-[#F4F8FF] to-white">
        <div
            class="pointer-events-none absolute inset-0 opacity-[0.05]"
            :style="{ backgroundImage: 'radial-gradient(circle, #E63946 1px, transparent 1px)', backgroundSize: '32px 32px' }"
        />
        <div class="pointer-events-none absolute top-20 left-10 w-80 h-80 rounded-full bg-brand-blue-10 blur-3xl" />
        <div class="pointer-events-none absolute bottom-20 right-10 w-72 h-72 rounded-full bg-brand-cyan-10 blur-3xl" />

        <div class="relative mx-auto max-w-7xl px-5 sm:px-8">
            <div class="learning-system-layout">
                <div>
                    <div class="flex justify-center lg:justify-start">
                        <span class="inline-flex items-center px-4 py-1.5 rounded-full bg-brand-blue-10 text-brand-blue text-sm font-bold">
                            طريقة التعلم
                        </span>
                    </div>

                    <div class="learning-system-header mt-6 text-center lg:text-right">
                        <h2 class="text-4xl md:text-6xl font-extrabold leading-[1.15] text-brand-navy">
                            منظومة
                            <span class="bg-gradient-to-l from-brand-blue to-brand-cyan bg-clip-text text-transparent">
                                كاملة
                            </span>
                        </h2>
                        <p class="mt-4 text-2xl md:text-3xl font-extrabold text-brand-navy">
                            مش كورس فيديوهات وخلاص
                        </p>
                        <p class="mt-6 text-brand-navy-65 leading-relaxed max-w-2xl mx-auto lg:mx-0 lg:ml-auto lg:mr-0 text-right">
                            كل جزء في البرمجة مع غنيم معمول عشان الطالب يفهم، يطبّق، يراجع، ويسأل… مش بس يتفرج على شرح وينسى.
                        </p>
                    </div>

                    <div class="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        <div
                            v-for="(f, i) in learningFeatures"
                            :key="f.title"
                            class="group relative bg-white rounded-2xl p-6 border border-brand-navy-5 shadow-[0_4px_20px_-8px_rgba(15,23,42,0.08)] hover:shadow-[0_12px_30px_-10px_rgba(37,99,235,0.25)] hover:-translate-y-1 transition-all duration-300"
                        >
                            <div class="flex items-start justify-between">
                                <div :class="['h-11 w-11 rounded-xl flex items-center justify-center shadow-md learning-feature-icon', iconClass(f.color)]">
                                    <lucide-icon :name="f.icon" class="h-5 w-5 text-white" />
                                </div>
                                <span :class="['font-mono font-extrabold text-lg learning-feature-num', numClass(f.color)]">{{ featureNum(i) }}</span>
                            </div>
                            <h4 class="mt-6 font-extrabold text-brand-navy">{{ f.title }}</h4>
                            <p class="mt-2 text-sm text-brand-navy-65 leading-relaxed">{{ f.text }}</p>
                        </div>
                    </div>

                    <div class="mt-14 relative bg-gradient-to-l from-brand-blue-10 to-brand-cyan-10 border border-brand-blue-15 rounded-2xl p-7 md:p-8">
                        <div class="absolute -top-4 right-6 h-10 w-10 rounded-full bg-brand-blue text-white flex items-center justify-center shadow-lg">
                            <lucide-icon name="MessageCircle" class="h-5 w-5" />
                        </div>
                        <p class="text-center md:text-right font-extrabold text-brand-navy text-lg md:text-xl leading-relaxed">
                            الطالب اللي يتفرج بس
                            <span class="text-red-500">هينسى</span>…
                            الطالب اللي يكتب ويجرب
                            <span class="bg-gradient-to-l from-brand-blue to-brand-cyan bg-clip-text text-transparent">
                                هو اللي هيفهم
                            </span>
                        </p>
                        <p class="mt-2 text-center md:text-right text-sm text-brand-navy-60">
                            ده هو الفرق بين منصة شرح عادية… ومنظومة تعلم حقيقية.
                        </p>
                    </div>
                </div>

                <div class="learning-system-portrait relative h-[680px] sticky top-24 self-start">
                    <div class="absolute inset-x-2 inset-y-4 rounded-[2.5rem] bg-gradient-to-br from-white via-[#F4F8FF] to-[#EAF2FF] border border-white shadow-[0_30px_80px_-30px_rgba(37,99,235,0.35)]" />

                    <div
                        class="absolute top-6 right-6 opacity-40 pointer-events-none"
                        :style="{ backgroundImage: 'radial-gradient(circle, #E63946 1.2px, transparent 1.2px)', backgroundSize: '10px 10px', width: '70px', height: '70px' }"
                    />

                    <svg class="absolute inset-0 m-auto w-[420px] h-[420px] top-12 pointer-events-none" viewBox="0 0 200 200" fill="none" aria-hidden="true">
                        <defs>
                            <linearGradient id="orbitG" x1="0" x2="1" y1="0" y2="1">
                                <stop offset="0" stop-color="#22D3EE" stop-opacity="0.85" />
                                <stop offset="1" stop-color="#E63946" stop-opacity="0.85" />
                            </linearGradient>
                        </defs>
                        <circle cx="100" cy="100" r="95" stroke="url(#orbitG)" stroke-width="0.7" stroke-dasharray="2 4" />
                        <circle cx="100" cy="100" r="78" stroke="url(#orbitG)" stroke-width="0.6" stroke-dasharray="1 5" opacity="0.7" />
                        <circle cx="100" cy="5" r="2.2" fill="#22D3EE" />
                        <circle cx="195" cy="100" r="2.2" fill="#E63946" />
                        <circle cx="100" cy="195" r="2.2" fill="#22D3EE" />
                        <circle cx="5" cy="100" r="2.2" fill="#E63946" />
                        <circle cx="170" cy="30" r="1.6" fill="#22D3EE" opacity="0.7" />
                        <circle cx="30" cy="170" r="1.6" fill="#E63946" opacity="0.7" />
                    </svg>

                    <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div class="w-[380px] h-[380px] rounded-full bg-gradient-to-br from-brand-blue-25 via-brand-cyan-15 to-transparent blur-3xl" />
                    </div>

                    <div class="absolute inset-0 flex items-end justify-center">
                        <img
                            :src="ecosystemImage"
                            alt="محمد غنيم يشرح نظام التعلم — البرمجة مع غنيم"
                            loading="lazy"
                            decoding="async"
                            class="relative h-[660px] w-auto object-contain drop-shadow-[0_25px_45px_rgba(37,99,235,0.30)] z-10"
                        />
                    </div>

                    <div class="absolute top-20 left-4 z-20 animate-float">
                        <div class="px-3.5 py-2 rounded-xl bg-white border border-brand-blue-20 shadow-[0_10px_25px_-10px_rgba(37,99,235,0.45)] font-mono text-sm font-extrabold text-brand-blue">
                            &lt;/&gt;
                        </div>
                    </div>

                    <div class="absolute top-[42%] left-2 z-20 animate-float-slow">
                        <div class="px-3.5 py-2 rounded-xl bg-white border border-brand-cyan-30 shadow-[0_10px_25px_-10px_rgba(34,211,238,0.45)] font-mono text-sm font-extrabold text-brand-cyan">
                            { }
                        </div>
                    </div>

                    <div class="absolute top-[40%] right-4 z-20 animate-float">
                        <div class="px-3.5 py-2 rounded-xl bg-brand-blue-10 border border-brand-blue-25 backdrop-blur-sm shadow-md font-mono text-[11px] font-extrabold text-brand-blue leading-tight text-center">
                            0101<br>1010
                        </div>
                    </div>

                    <div class="absolute bottom-32 left-2 z-20 animate-float">
                        <div class="h-12 w-12 rounded-2xl bg-gradient-to-br from-brand-green to-emerald-500 text-white shadow-[0_12px_28px_-8px_rgba(34,197,94,0.55)] flex items-center justify-center font-extrabold text-sm">
                            JS
                        </div>
                    </div>

                    <div class="absolute bottom-12 left-6 z-20 animate-float-slow">
                        <div class="flex items-center gap-2 px-3.5 py-2 rounded-full bg-white border border-brand-green-30 shadow-[0_10px_25px_-10px_rgba(34,197,94,0.5)]">
                            <span class="h-5 w-5 rounded-full bg-brand-green flex items-center justify-center">
                                <lucide-icon name="CheckCircle2" class="h-3.5 w-3.5 text-white" />
                            </span>
                            <span class="font-mono font-extrabold text-sm text-brand-navy">100/100</span>
                        </div>
                    </div>

                    <div class="absolute bottom-24 right-2 z-20 animate-float-slow">
                        <div class="px-3.5 py-2 rounded-xl bg-gradient-to-br from-brand-cyan to-brand-blue text-white shadow-[0_12px_28px_-8px_rgba(37,99,235,0.55)] font-extrabold text-xs">
                            Coding Lab
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import LucideIcon from './LucideIcon.vue';
import { learningFeatures } from './landing-data';

export default {
    name: 'WelcomeLearningSystem',
    components: { LucideIcon },
    data() {
        return {
            ecosystemImage: require('@/assets/welcome/ghonaim-ecosystem.webp'),
            learningFeatures,
        };
    },
    methods: {
        iconClass(color) {
            return `learning-feature-icon--${color}`;
        },
        numClass(color) {
            return `learning-feature-num--${color}`;
        },
        featureNum(index) {
            return String(index + 1).padStart(2, '0');
        },
    },
};
</script>
